// `/admin/webhooks/deliveries` (croqui `screens/admin/webhooks-enviados.tsx`). Foco: **a
// tabela de entregas**.
//
// Sem rota `$id`: `files_allowed` desta task só tem a rota-índice, então o detalhe abre
// pelo `?id=whd_…` da própria query string (mesmo corte do item 1 das "Questões abertas"
// da WEB-57). O que a decisão 5 de `spec/24` §1 pede continua valendo, a URL carrega o
// detalhe, deep link renderiza a lista embaixo e fechar volta com o filtro intacto.
//
// Modo é filtro de tela, não toggle global (`spec/17` §4.4): a rota admin recebe `mode`
// como query e a API assume `live` quando ele não vai. Na URL vai só o id, nunca a
// `target_url`, dado de outro seller.

import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { adminDeliveriesListOptions } from '../api/queries/admin-webhooks.ts';
import { AdminDeliveriesScreen } from '../features/admin-webhooks/deliveries-screen.tsx';
import { AdminDeliveryDrawer } from '../features/admin-webhooks/delivery-drawer.tsx';
import { DELIVERY_TAB_IDS, matchesDeliverySearch } from '../features/admin-webhooks/model.ts';

const MODES = ['live', 'test'] as const;

/** Query string tolerante a lixo: `?status=banana` cai em `all` (`catch` do Zod). */
const searchSchema = z.object({
  status: z.enum(DELIVERY_TAB_IDS).default('all').catch('all'),
  mode: z.enum(MODES).optional().catch(undefined),
  seller_id: z.string().min(1).optional().catch(undefined),
  id: z.string().min(1).optional().catch(undefined),
});

function AdminDeliveries() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  const filters = {
    ...(search.status === 'all' ? {} : { status: search.status }),
    ...(search.mode === undefined ? {} : { mode: search.mode }),
    ...(search.seller_id === undefined ? {} : { seller_id: search.seller_id }),
  };

  const list = useInfiniteQuery(adminDeliveriesListOptions(filters));
  const loaded = list.data?.pages.flatMap((page) => page.data) ?? [];
  const rows = loaded.filter((row) => matchesDeliverySearch(row, term));
  const selected = loaded.find((row) => row.id === search.id);

  const apply = (next: Partial<z.infer<typeof searchSchema>>): void => {
    void navigate({
      to: '/admin/webhooks/deliveries',
      search: { ...search, ...next },
      replace: true,
    });
  };

  return (
    <>
      <AdminDeliveriesScreen
        rows={rows}
        tab={search.status}
        onTabChange={(next) => {
          apply({ status: next });
        }}
        sellerId={search.seller_id}
        onSellerChange={(id) => {
          apply({ seller_id: id });
        }}
        mode={search.mode}
        onModeChange={(mode) => {
          apply({ mode });
        }}
        search={term}
        onSearchChange={setTerm}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
        hasMore={list.hasNextPage}
        isLoadingMore={list.isFetchingNextPage}
        onLoadMore={() => {
          void list.fetchNextPage();
        }}
        onSelect={(row) => {
          apply({ id: row.id });
        }}
        {...(search.id === undefined ? {} : { selectedId: search.id })}
        onClearFilters={() => {
          // "Limpar filtros" NÃO troca o modo: mudar de `test` para `live` sem pedir
          // trocaria o conjunto de dados debaixo do operador (WEB-75 item 2).
          setTerm('');
          apply({ status: 'all', seller_id: undefined });
        }}
      />

      <AdminDeliveryDrawer
        open={search.id !== undefined}
        row={selected}
        onClose={() => {
          apply({ id: undefined });
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/webhooks/deliveries/')({
  validateSearch: searchSchema,
  component: AdminDeliveries,
});
